import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Form,
  Input,
  Select,
  Button,
  Message,
  Space,
  InputNumber,
  Card,
  Divider,
  Upload,
  Tag,
  Progress
} from '@arco-design/web-react';
import {
  IconBook,
  IconPlus,
  IconDelete,
  IconFile,
  IconUpload,
  IconCheck,
  IconClose
} from '@arco-design/web-react/icon';
import { getApiUrl } from '@/config/api';
import { useSaveAdminDetails, useUploadDocument, useDeleteDocument, useSaveDocumentNotes } from '../hooks/useOnboardingData';
import './TeacherDetailsStep.css';
import ReportProblemButton from '../components/ReportProblemButton';
import './DocumentUploadCard.css';
import DocumentPreviewModal from './DocumentPreviewModal';

const FormItem = Form.Item;
const { Option } = Select;

const calculateHighestDegree = (degrees) => {
  if (!degrees || degrees.length === 0) return null;

  const degreeHierarchy = {
    licenciatura: 1,
    maestria: 2,
    doctorado: 3
  };

  let highest = null;
  let highestValue = 0;

  degrees.forEach((degree) => {
    const value = degreeHierarchy[degree.level] || 0;
    if (value > highestValue) {
      highestValue = value;
      highest = degree.level;
    }
  });

  return highest;
};

function AdminDetailsStep({
  onNext,
  onBack,
  initialData,
  documents: initialDocuments,
  requestedChanges,
  section = 'academic',
  previewMode = false,
  freeMode = false
}) {
  const { t } = useTranslation('onboarding');
  const [form] = Form.useForm();
  const sectionConfig = {
    academic: {
      title: t('steps.admin.sections.academic.title'),
      description: t('steps.admin.sections.academic.description')
    },
    professional: {
      title: t('steps.admin.sections.professional.title'),
      description: t('steps.admin.sections.professional.description')
    }
  };

  const saveMutation = useSaveAdminDetails();
  const draftMutation = useSaveAdminDetails({ partial: true });
  const uploadMutation = useUploadDocument();
  const deleteMutation = useDeleteDocument();
  const notesMutation = useSaveDocumentNotes();

  const [degrees, setDegrees] = useState([
    { level: '', institution: '', field: '', graduation_status: 'graduado', graduation_year: null }
  ]);
  const [previousInstitutions, setPreviousInstitutions] = useState([]);
  const [documents, setDocuments] = useState({});
  const [notes, setNotes] = useState('');
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewVisible, setPreviewVisible] = useState(false);

  const showAcademic = section === 'academic';
  const showProfessional = section === 'professional';

  const changeMap = useMemo(() => {
    const map = {};
    (requestedChanges || [])
      .filter((change) => change.step === 'teacher_details' && !change.resolved)
      .forEach((change) => {
        map[change.field] = change.message;
      });
    return map;
  }, [requestedChanges]);

  const documentChangeMap = useMemo(() => {
    const map = {};
    (requestedChanges || [])
      .filter((change) => change.step === 'documents' && !change.resolved)
      .forEach((change) => {
        map[change.field] = change.message;
      });
    return map;
  }, [requestedChanges]);

  const getFieldProps = (field) => {
    const message = changeMap[field];
    if (!message) return {};
    return {
      validateStatus: 'error',
      help: message
    };
  };

  useEffect(() => {
    if (initialData) {
      form.setFieldsValue({
        years_work_experience: initialData.years_work_experience ?? 0,
        linkedin_url: initialData.linkedin_url || '',
        portfolio_url: initialData.portfolio_url || ''
      });

      if (initialData.degrees && initialData.degrees.length > 0) {
        const normalized = initialData.degrees.map((degree) => ({
          graduation_status: degree.graduation_status || (degree.graduation_year ? 'graduado' : 'cursando'),
          graduation_year: degree.graduation_year ?? null,
          ...degree
        }));
        setDegrees(normalized);
      }

      if (initialData.previous_institutions && initialData.previous_institutions.length > 0) {
        setPreviousInstitutions(initialData.previous_institutions);
      }
    }
  }, [initialData, form]);

  useEffect(() => {
    setDocuments(initialDocuments || {});
    setNotes(initialDocuments?.notes || '');
  }, [initialDocuments]);

  const validateAcademic = () => {
    if (degrees.length === 0 || !degrees[0].level) {
      Message.error(t('shared.messages.addDegreeRequired'));
      return false;
    }

    const missingDegree = degrees.find((degree) => {
      const baseMissing = !degree.level || !degree.institution || !degree.field;
      if (baseMissing) return true;
      if ((degree.graduation_status || 'graduado') === 'cursando') {
        return false;
      }
      return !degree.graduation_year;
    });

    if (missingDegree) {
      Message.error(t('shared.messages.completeDegreeData'));
      return false;
    }

    return true;
  };

  const validateProfessional = (values) => {
    if (values.years_work_experience === undefined || values.years_work_experience === null) {
      Message.error(t('steps.admin.messages.workExperienceRequired'));
      return false;
    }

    const invalidInstitution = previousInstitutions.find((inst) => {
      if (!inst) return false;
      return !inst.name || !inst.position || !inst.responsibilities || !inst.achievements || inst.years === undefined || inst.years === null;
    });

    if (invalidInstitution) {
      Message.error(t('steps.admin.messages.completeInstitutionData'));
      return false;
    }

    return true;
  };

  const buildAcademicPayload = () => ({
    highest_degree: calculateHighestDegree(degrees),
    degree_count: degrees.length,
    degrees
  });

  const buildProfessionalPayload = (values) => ({
    years_work_experience: values.years_work_experience,
    previous_institutions: previousInstitutions.length > 0 ? previousInstitutions : [],
    linkedin_url: values.linkedin_url || null,
    portfolio_url: values.portfolio_url || null
  });

  const saveNotes = (value, { silent = false } = {}) => {
    if (previewMode) {
      if (!silent) {
        Message.success(t('shared.messages.simulationNotesSaved'));
      }
      return;
    }
    const normalized = (value || '').trim();
    const currentNotes = (documents?.notes || '').trim();

    if (normalized === currentNotes) {
      return;
    }

    notesMutation.mutate(normalized, {
      onSuccess: () => {
        setDocuments((prev) => ({ ...prev, notes: normalized }));
        if (!silent) {
          Message.success(t('shared.messages.notesSaved'));
        }
      },
      onError: (error) => {
        console.error('Error al guardar notas:', error);
        if (!silent) {
          Message.error(t('shared.messages.notesError'));
        }
      }
    });
  };

  const handleSubmit = (values) => {
    if (previewMode) {
      if (showAcademic) {
        if (!validateAcademic()) {
          return;
        }
        Message.success(t('steps.admin.messages.simulationAcademicValidated'));
        onNext(null);
        return;
      }

      if (!validateProfessional(values)) {
        return;
      }
      Message.success(t('steps.admin.messages.simulationProfessionalValidated'));
      onNext(null);
      return;
    }

    if (showAcademic) {
      if (!validateAcademic()) {
        return;
      }

      saveMutation.mutate(buildAcademicPayload(values), {
        onSuccess: () => {
          Message.success(t('steps.admin.messages.academicSaved'));
          onNext(null);
        },
        onError: (error) => {
          console.error('Error al guardar admin details:', error);
          if (error.response?.data?.detail) {
            Message.error(error.response.data.detail);
          } else {
            Message.error(t('shared.messages.errorSaving'));
          }
        }
      });
      return;
    }

    if (!validateProfessional(values)) {
      return;
    }

    saveMutation.mutate(buildProfessionalPayload(values), {
      onSuccess: () => {
        saveNotes(notes, { silent: true });
        Message.success(t('steps.admin.messages.professionalSaved'));
        onNext(null);
      },
      onError: (error) => {
        console.error('Error al guardar admin details:', error);
        if (error.response?.data?.detail) {
          Message.error(error.response.data.detail);
        } else {
          Message.error(t('shared.messages.errorSaving'));
        }
      }
    });
  };

  const handleSaveDraft = () => {
    if (previewMode) {
      Message.success(t('shared.messages.simulationDraftSaved'));
      return;
    }
    const values = form.getFieldsValue();
    if (showAcademic) {
      if (!validateAcademic()) {
        return;
      }
      draftMutation.mutate(buildAcademicPayload(values), {
        onSuccess: () => {
          Message.success(t('shared.messages.dataSavedDraft'));
        },
        onError: (error) => {
          console.error('Error al guardar borrador:', error);
          Message.error(t('shared.messages.errorSavingDraft'));
        }
      });
      return;
    }

    if (!validateProfessional(values)) {
      return;
    }

    draftMutation.mutate(buildProfessionalPayload(values), {
      onSuccess: () => {
        saveNotes(notes, { silent: true });
        Message.success(t('shared.messages.dataSavedDraft'));
      },
      onError: (error) => {
        console.error('Error al guardar borrador:', error);
        Message.error(t('shared.messages.errorSavingDraft'));
      }
    });
  };

  const addDegree = () => {
    setDegrees([
      ...degrees,
      { level: '', institution: '', field: '', graduation_status: 'graduado', graduation_year: null }
    ]);
  };

  const removeDegree = (index) => {
    if (degrees.length > 1) {
      setDegrees(degrees.filter((_, i) => i !== index));
    }
  };

  const updateDegree = (index, field, value) => {
    const updated = [...degrees];
    updated[index] = {
      ...updated[index],
      [field]: value
    };
    if (field === 'graduation_status' && value === 'cursando') {
      updated[index].graduation_year = null;
    }
    setDegrees(updated);
  };

  const addPreviousInstitution = () => {
    setPreviousInstitutions([
      ...previousInstitutions,
      {
        name: '',
        years: null,
        position: '',
        responsibilities: '',
        achievements: ''
      }
    ]);
  };

  const removePreviousInstitution = (index) => {
    setPreviousInstitutions(previousInstitutions.filter((_, i) => i !== index));
  };

  const updatePreviousInstitution = (index, field, value) => {
    const updated = [...previousInstitutions];
    updated[index] = {
      ...updated[index],
      [field]: value
    };
    setPreviousInstitutions(updated);
  };

  const openPreview = (doc, title) => {
    setPreviewDoc(doc);
    setPreviewTitle(title || doc?.file_name || t('shared.labels.document'));
    setPreviewVisible(true);
  };

  const closePreview = () => {
    setPreviewVisible(false);
    setPreviewDoc(null);
    setPreviewTitle('');
  };

  const renderDocumentThumbnail = (doc) => {
    if (!doc) return null;
    const fileUrl = doc.file_url || doc.file_path || `${getApiUrl()}${doc.file_path}`;
    const fileName = doc?.file_name || t('shared.labels.document');
    const ext = fileName.split('.').pop().toLowerCase();

    if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) {
      return <img src={fileUrl} alt={fileName} className="document-thumbnail-image" />;
    }

    if (ext === 'pdf') {
      return (
        <object data={fileUrl} type="application/pdf" className="document-thumbnail-frame">
          <div className="document-thumbnail-fallback">
            <IconFile />
            <small>PDF</small>
          </div>
        </object>
      );
    }

    return (
      <div className="document-thumbnail-fallback">
        <IconFile />
        <small>{ext.toUpperCase()}</small>
      </div>
    );
  };

  const handleUpload = (file, documentKey) => {
    if (previewMode) {
      setDocuments((prev) => ({
        ...prev,
        [documentKey]: {
          file_name: file?.name || t('shared.labels.document'),
          file_size: file?.size || 0,
          file_url: '',
          file_path: ''
        }
      }));
      Message.success(t('shared.messages.simulationDocUploaded'));
      return false;
    }

    uploadMutation.mutate({ file, documentType: documentKey }, {
      onSuccess: (response) => {
        const result = response?.data || response;
        const uploadedDocument = result?.document;
        if (!uploadedDocument) {
          Message.error(t('shared.messages.documentNoInfo'));
          return;
        }

        setDocuments((prev) => ({
          ...prev,
          [documentKey]: uploadedDocument
        }));
        Message.success(t('shared.messages.documentUploaded'));
      },
      onError: (error) => {
        console.error('Error al subir documento:', error);
        Message.error(t('shared.messages.documentUploadError'));
      }
    });

    return false;
  };

  const handleDelete = (documentKey) => {
    if (previewMode) {
      setDocuments((prev) => {
        const next = { ...prev };
        delete next[documentKey];
        return next;
      });
      Message.success(t('shared.messages.simulationDocDeleted'));
      return;
    }

    deleteMutation.mutate(documentKey, {
      onSuccess: () => {
        setDocuments((prev) => {
          const next = { ...prev };
          delete next[documentKey];
          return next;
        });
        Message.success(t('shared.messages.documentDeleted'));
      },
      onError: (error) => {
        console.error('Error al eliminar documento:', error);
        Message.error(t('shared.messages.documentDeleteError'));
      }
    });
  };

  const renderUploadCard = ({ key, label, description, required, changeMessage }) => {
    const isUploaded = !!documents[key];
    const isUploading = uploadMutation.isPending && uploadMutation.variables?.documentType === key;
    const cardClassName = `document-upload-card${changeMessage ? ' document-upload-card--change' : ''}`;

    return (
      <Card className={cardClassName} bordered>
        <div className="document-card-header">
          <div className="document-title">
            <IconFile />
            <span>{label}</span>
            {required && <Tag color="red">{t('shared.labels.required')}</Tag>}
          </div>
          {changeMessage && <div className="document-change-note">{changeMessage}</div>}
        </div>
        <div className="document-description">{description}</div>

        {isUploading ? (
          <Progress percent={50} showText />
        ) : (
          <div className="document-actions">
            {isUploaded ? (
              <>
                <div className="document-info">
                  <div
                    className="document-thumbnail"
                    onClick={() => openPreview(documents[key], label)}
                    aria-label={t('shared.labels.viewDocument')}
                  >
                    {renderDocumentThumbnail(documents[key])}
                  </div>
                  <div className="document-meta">
                    <small>{documents[key].file_name}</small>
                    <small>{(documents[key].file_size / 1024).toFixed(1)} KB</small>
                  </div>
                </div>
                <Space>
                  <Button
                    type="primary"
                    icon={<IconCheck />}
                    onClick={() => openPreview(documents[key], label)}
                  >
                    {t('shared.labels.view')}
                  </Button>
                  <Button
                    type="text"
                    status="danger"
                    icon={<IconClose />}
                    onClick={() => handleDelete(key)}
                  >
                    {t('shared.labels.delete')}
                  </Button>
                </Space>
              </>
            ) : (
              <Upload
                showUploadList={false}
                beforeUpload={(file) => handleUpload(file, key)}
                accept=".pdf,.jpg,.jpeg,.png"
              >
                <Button icon={<IconUpload />}>{t('shared.buttons.uploadDocument')}</Button>
              </Upload>
            )}
          </div>
        )}
      </Card>
    );
  };

  const currentYear = new Date().getFullYear();

  return (
    <div className="teacher-details-step">
      <div className="step-header">
        <h2>{sectionConfig[section]?.title || t('steps.admin.defaultTitle')}</h2>
        <p>{sectionConfig[section]?.description || ''}</p>
      </div>

      <Form
        form={form}
        layout="vertical"
        onSubmit={handleSubmit}
        initialValues={{
          years_work_experience: 0
        }}
      >
        {showAcademic && (
          <div className="form-section">
            <h3>
              <IconBook style={{ marginRight: 8 }} />
              {t('shared.sections.academicBackground')}
            </h3>

            <Divider orientation="left">{t('shared.sections.academicDegrees', { count: degrees.length })}</Divider>

            {degrees.map((degree, index) => (
              <Card
                key={index}
                style={{ marginBottom: 16 }}
                title={t('shared.labels.degreeTitle', { number: index + 1 })}
                extra={
                  degrees.length > 1 && (
                    <Button
                      type="text"
                      status="danger"
                      icon={<IconDelete />}
                      onClick={() => removeDegree(index)}
                    >
                      {t('shared.labels.delete')}
                    </Button>
                  )
                }
              >
                <FormItem label={t('shared.labels.level')} required {...getFieldProps(`degrees.${index}.level`)}>
                  <Select
                    placeholder={t('shared.placeholders.degreeLevel')}
                    value={degree.level}
                    onChange={(value) => updateDegree(index, 'level', value)}
                  >
                    <Option value="licenciatura">{t('shared.degrees.licenciatura')}</Option>
                    <Option value="maestria">{t('shared.degrees.maestria')}</Option>
                    <Option value="doctorado">{t('shared.degrees.doctorado')}</Option>
                  </Select>
                </FormItem>

                <FormItem label={t('shared.labels.institution')} required {...getFieldProps(`degrees.${index}.institution`)}>
                  <Input
                    placeholder={t('shared.placeholders.institution')}
                    value={degree.institution}
                    onChange={(value) => updateDegree(index, 'institution', value)}
                  />
                </FormItem>

                <FormItem label={t('shared.labels.degree')} required {...getFieldProps(`degrees.${index}.field`)}>
                  <Input
                    placeholder={t('shared.placeholders.field')}
                    value={degree.field}
                    onChange={(value) => updateDegree(index, 'field', value)}
                  />
                </FormItem>

                <FormItem label={t('shared.labels.graduationStatus')} required>
                  <Select
                    value={degree.graduation_status || 'graduado'}
                    onChange={(value) => updateDegree(index, 'graduation_status', value)}
                  >
                    <Option value="graduado">{t('shared.degrees.graduado')}</Option>
                    <Option value="cursando">{t('shared.degrees.cursando')}</Option>
                  </Select>
                </FormItem>

                <FormItem
                  label={t('shared.labels.graduationYear')}
                  required={degree.graduation_status !== 'cursando'}
                  {...getFieldProps(`degrees.${index}.graduation_year`)}
                >
                  <InputNumber
                    placeholder={t('shared.placeholders.year2015')}
                    min={1950}
                    max={currentYear}
                    value={degree.graduation_year}
                    onChange={(value) => updateDegree(index, 'graduation_year', value)}
                    style={{ width: '100%' }}
                    disabled={degree.graduation_status === 'cursando'}
                  />
                </FormItem>

                <Divider orientation="left">{t('shared.documents.degreeDocTitle')}</Divider>
                {renderUploadCard({
                  key: `degree_${index}`,
                  label: t('shared.documents.degreeDocLabel', { number: index + 1 }),
                  description: `${degree.level ? degree.level.charAt(0).toUpperCase() + degree.level.slice(1) : t('shared.labels.degreeGeneric')}${degree.field ? ` - ${degree.field}` : ''}${degree.institution ? ` (${degree.institution})` : ''}`.trim(),
                  required: true,
                  changeMessage: documentChangeMap[`degree_${index}`]
                })}
              </Card>
            ))}

            <Button type="dashed" long icon={<IconPlus />} onClick={addDegree}>
              {t('shared.buttons.addDegree')}
            </Button>

            <Divider orientation="left">{t('shared.documents.professionalLicenseTitle')}</Divider>
            {renderUploadCard({
              key: 'professional_license',
              label: t('shared.documents.professionalLicenseTitle'),
              description: t('shared.documents.professionalLicenseDesc'),
              required: false,
              changeMessage: documentChangeMap.professional_license
            })}
          </div>
        )}

        {showProfessional && (
          <div className="form-section">
            <h3>
              <IconBook style={{ marginRight: 8 }} />
              {t('steps.admin.labels.workExperience')}
            </h3>

            <FormItem
              label={t('steps.admin.labels.workExperienceYears')}
              field="years_work_experience"
              rules={[
                { required: true, message: t('steps.admin.messages.workExperienceRequired') },
                { type: 'number', min: 0, max: 50 }
              ]}
              {...getFieldProps('years_work_experience')}
            >
              <InputNumber placeholder={t('shared.placeholders.experience')} min={0} max={50} style={{ width: '100%' }} />
            </FormItem>

            <Divider orientation="left">{t('steps.admin.labels.previousInstitutions')}</Divider>

            {previousInstitutions.map((inst, index) => (
              <Card
                key={index}
                style={{ marginBottom: 16 }}
                title={t('shared.labels.institutionTitle', { number: index + 1 })}
                extra={
                  <Button
                    type="text"
                    status="danger"
                    icon={<IconDelete />}
                    onClick={() => removePreviousInstitution(index)}
                  >
                    {t('shared.labels.delete')}
                  </Button>
                }
              >
                <FormItem label={t('steps.admin.labels.institutionName')} required {...getFieldProps(`previous_institutions.${index}.name`)}>
                  <Input
                    placeholder={t('steps.teacher.placeholders.institutionName')}
                    value={inst.name}
                    onChange={(value) => updatePreviousInstitution(index, 'name', value)}
                  />
                </FormItem>

                <FormItem label={t('steps.admin.labels.position')} required {...getFieldProps(`previous_institutions.${index}.position`)}>
                  <Input
                    placeholder={t('steps.admin.placeholders.position')}
                    value={inst.position}
                    onChange={(value) => updatePreviousInstitution(index, 'position', value)}
                  />
                </FormItem>

                <FormItem label={t('steps.admin.labels.responsibilities')} required {...getFieldProps(`previous_institutions.${index}.responsibilities`)}>
                  <Input
                    placeholder={t('steps.admin.placeholders.responsibilities')}
                    value={inst.responsibilities}
                    onChange={(value) => updatePreviousInstitution(index, 'responsibilities', value)}
                  />
                </FormItem>

                <FormItem label={t('steps.admin.labels.achievements')} required {...getFieldProps(`previous_institutions.${index}.achievements`)}>
                  <Input
                    placeholder={t('steps.admin.placeholders.achievements')}
                    value={inst.achievements}
                    onChange={(value) => updatePreviousInstitution(index, 'achievements', value)}
                  />
                </FormItem>

                <FormItem label={t('steps.admin.labels.years')} required {...getFieldProps(`previous_institutions.${index}.years`)}>
                  <InputNumber
                    placeholder={t('shared.placeholders.years')}
                    min={0}
                    max={50}
                    value={inst.years}
                    onChange={(value) => updatePreviousInstitution(index, 'years', value)}
                    style={{ width: '100%' }}
                  />
                </FormItem>
              </Card>
            ))}

            <Button type="dashed" long icon={<IconPlus />} onClick={addPreviousInstitution}>
              {t('shared.buttons.addInstitution')}
            </Button>
          </div>
        )}

        {showProfessional && (
          <>
            <div className="form-section">
              <h3>
                <IconFile style={{ marginRight: 8 }} />
                {t('shared.sections.professionalDocumentation')}
              </h3>
              {renderUploadCard({
                key: 'cv',
                label: t('shared.documents.cvLabel'),
                description: t('shared.documents.cvDesc'),
                required: true,
                changeMessage: documentChangeMap.cv
              })}
            </div>

            <div className="form-section">
              <h3>
                <IconBook style={{ marginRight: 8 }} />
                {t('shared.sections.additionalInfoOptional')}
              </h3>

              <FormItem
                label={t('steps.teacher.labels.linkedinUrl')}
                field="linkedin_url"
                rules={[
                  {
                    match: /^https?:\/\/(www\.)?linkedin\.com\/.+$/,
                    message: t('shared.validation.linkedinUrl')
                  }
                ]}
                {...getFieldProps('linkedin_url')}
              >
                <Input placeholder={t('steps.teacher.placeholders.linkedin')} />
              </FormItem>

              <FormItem
                label={t('steps.teacher.labels.portfolioUrl')}
                field="portfolio_url"
                rules={[
                  {
                    match: /^https?:\/\/.+$/,
                    message: t('shared.validation.validUrl')
                  }
                ]}
                {...getFieldProps('portfolio_url')}
              >
                <Input placeholder={t('steps.teacher.placeholders.portfolio')} />
              </FormItem>
            </div>

            <div className="form-section">
              <h3>
                <IconFile style={{ marginRight: 8 }} />
                {t('shared.sections.hiringNotes')}
              </h3>
              <FormItem>
                <Input.TextArea
                  value={notes}
                  onChange={setNotes}
                  onBlur={() => saveNotes(notes)}
                  placeholder={t('steps.teacher.placeholders.hiringNotes')}
                  autoSize={{ minRows: 3, maxRows: 6 }}
                />
              </FormItem>
              <small style={{ color: 'var(--color-text-3)' }}>
                {t('shared.sections.hiringNotesHint')}
              </small>
            </div>
          </>
        )}

        <div className="form-actions">
          <Space size="large" style={{ width: '100%', justifyContent: 'center' }}>
            <Button size="large" onClick={onBack}>
              {t('shared.buttons.back')}
            </Button>
            <Button
              size="large"
              onClick={handleSaveDraft}
              loading={draftMutation.isPending}
              className="onboarding-draft-button"
            >
              {t('shared.buttons.saveDraft')}
            </Button>
            <Button
              type="primary"
              size="large"
              htmlType="submit"
              loading={saveMutation.isPending}
              disabled={notesMutation.isPending}
            >
              {t('shared.buttons.continue')}
            </Button>
            {previewMode && freeMode && (
              <Button size="large" onClick={() => onNext(null)}>
                {t('shared.buttons.skipValidation')}
              </Button>
            )}
            <ReportProblemButton stepLabel={sectionConfig[section]?.title || t('steps.admin.defaultTitle')} previewMode={previewMode} asButton />
          </Space>
        </div>
      </Form>

      <DocumentPreviewModal
        visible={previewVisible}
        onClose={closePreview}
        document={previewDoc}
        title={previewTitle}
      />
    </div>
  );
}

export default AdminDetailsStep;
